import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, LogOut, Save } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCompanyProfileT } from '../../locales/companyProfile';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { resolveImageUrl } from '../../services/imageUrlService';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const TAB_COMPANY = 0;
const TAB_DEFAULTS = 1;
const TAB_SMS = 2;
const TAB_ACCOUNT = 3;
const TAB_SUPER = 4;

const EMPTY_PROFILE = {
  Id: '',
  Name: '',
  MobileNo: '',
  TelNo: '',
  Address: '',
  EmailId: '',
  Website: '',
  Logo: '',
  DefDelCharge: 0,
  DefaultTypeId: '',
  DefTAX: 0,
  DefPayType: '',
  DefChargeFrom: '',
  EmailUsername: '',
  EmailPassword: '',
  IsManualDeliverySMS: false,
  IsSameDay: false,
  IsVATFromDelivery: false,
  IsBlockByOrderSelection: false,
  IsPickupByComp: false,
  IsBlockCancelPay: false,
  IsKeepEmirate: false,
  IsSMSFromDN: false,
  IsSeperateClientPayment: false,
  AllowSameClientNo: false,
  AllowSameCustomerNo: false,
  AdjustCPDate: false,
  BlockPayRetn: false,
  BlockCustomerOrderLoad: false,
  OnlyDeliveredSMS: false,
  HideCODSelection: false,
  IsBlockSameStoreVoucherNo: false,
  IsBlockStoreRefLoad: false,
  IsHideOrderDelete: false,
  IsAutoGenerateVoucherNo: false,
  IsHidePaymentEditOnShipment: false,
  IsHideDeliveryEditOnShipment: false,
  IsHidePaymentEditOnOrder: false,
  IsHideDeliveryEditOnOrder: false,
  IsMaskClientMobNo: false,
  IsBlockExcelExport: false,
  IsBlockPdfReport: false,
  IsBlockIncomeInShipment: false,
  IsAccounting: false,
  IsHR: false,
  IsMap: false,
  IsPushNotifications: false,
  IsVATFromCustomerOnly: false,
  IsOtp: false,
  SMSUSername: '',
  SMSPassword: '',
  SMSId: '',
  FireApiKey: '',
  FireAuthDomain: '',
  FireProjectId: '',
  FireStorageBucket: '',
  FireMessagingSenderId: '',
  FireAppId: '',
  FireMeasurementId: '',
  FireServerKey: '',
  FinancialYearId: '',
};

function readUserInfo() {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}');
  } catch {
    return {};
  }
}

function isSuperAdmin(role) {
  return String(role || '').toUpperCase() === 'SA';
}

function isSaveSuccess(response) {
  if (response === true || response === 'true' || response === 'Success') return true;
  if (response && typeof response === 'object') {
    if (response.success === true || response.Success === true) return true;
  }
  return false;
}

function buildAccountFilter(term) {
  return {
    filter: {
      logic: 'and',
      filters: [{ field: 'Name', operator: 'contains', value: term, ignoreCase: true }],
    },
    page: 1,
    pageSize: 10,
  };
}

function parseAccountList(response) {
  if (Array.isArray(response)) return response;
  if (response?.Data && Array.isArray(response.Data)) return response.Data;
  return [];
}

function mergeProfile(loaded) {
  if (!loaded || typeof loaded !== 'object') return { ...EMPTY_PROFILE };
  return {
    ...EMPTY_PROFILE,
    ...loaded,
    DefaultInputVat: loaded.DefaultInputVat || { Name: '' },
    DefaultOutputVat: loaded.DefaultOutputVat || { Name: '' },
    DefaultClientHead: loaded.DefaultClientHead || { Name: '' },
    DefaultEmployeeHead: loaded.DefaultEmployeeHead || { Name: '' },
    DefaultExpenseHead: loaded.DefaultExpenseHead || { Name: '' },
    CustomerAccount: loaded.CustomerAccount || { Name: '' },
    DeliveryRevenewAcc: loaded.DeliveryRevenewAcc || { Name: '' },
    CashAccount: loaded.CashAccount || { Name: '' },
    BankAccount: loaded.BankAccount || { Name: '' },
  };
}

function SettingsField({ label, children, className = '' }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function SettingsCheckbox({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 hover:bg-gray-50">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-gray-800 leading-snug">{label}</span>
    </label>
  );
}

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function AccountAutocomplete({ value, onSelect, placeholder, level3 = false }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  const display = value?.Name || '';

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (term.length < 2) {
      setItems([]);
      return undefined;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const payload = buildAccountFilter(term);
        const response = level3
          ? await MainService.accountInAutoCompleteLevel3(payload)
          : await MainService.accountInAutoComplete(payload);
        setItems(parseAccountList(response));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [term, level3]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        className={inputClass}
        value={open ? term : display}
        placeholder={placeholder}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setTerm(display);
          setOpen(true);
        }}
      />
      {open && term.length >= 2 ? (
        <ul className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? (
            <li className="px-3 py-2 text-sm text-gray-500">…</li>
          ) : items.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">—</li>
          ) : (
            items.map((row) => (
              <li key={row.Id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onClick={() => {
                    onSelect(row);
                    setTerm('');
                    setOpen(false);
                  }}
                >
                  {row.Name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

const CompanyProfile = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useMemo(() => getCompanyProfileT(language), [language]);
  const userInfo = useMemo(() => readUserInfo(), []);
  const isSa = isSuperAdmin(userInfo.Role);

  const [activeTab, setActiveTab] = useState(TAB_COMPANY);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [types, setTypes] = useState([]);
  const [finYears, setFinYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiBase, setApiBase] = useState('');
  const fileRef = useRef(null);

  const patch = useCallback((updates) => {
    setProfile((prev) => ({ ...prev, ...updates }));
  }, []);

  const setFlag = useCallback((key, value) => {
    patch({ [key]: value });
  }, [patch]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setApiBase(getApiBaseUrl() || '');
      const [profileResp, typesResp, yearsResp] = await Promise.all([
        MainService.GetCompanyProfile(),
        MainService.TypesInDropDown().catch(() => []),
        MainService.FinYearInDropDown().catch(() => []),
      ]);
      setProfile(mergeProfile(profileResp));
      setTypes(Array.isArray(typesResp) ? typesResp : []);
      setFinYears(Array.isArray(yearsResp) ? yearsResp : []);
    } catch (err) {
      console.error('Company profile load:', err);
      setError(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (error) {
      const id = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [error]);

  useEffect(() => {
    if (success) {
      const id = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [success]);

  const logoPreview = useMemo(() => {
    const raw = profile.Logo;
    if (!raw) return '';
    if (String(raw).startsWith('data:') || String(raw).startsWith('blob:')) return raw;
    return resolveImageUrl(raw, apiBase);
  }, [profile.Logo, apiBase]);

  const tabs = useMemo(() => {
    const list = [
      { id: TAB_COMPANY, label: t('tabs.company') },
      { id: TAB_DEFAULTS, label: t('tabs.defaults') },
      { id: TAB_SMS, label: t('tabs.smsEmail') },
    ];
    if (profile.IsAccounting) list.push({ id: TAB_ACCOUNT, label: t('tabs.account') });
    if (isSa) list.push({ id: TAB_SUPER, label: t('tabs.superUser') });
    return list;
  }, [profile.IsAccounting, isSa, t]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? TAB_COMPANY);
    }
  }, [tabs, activeTab]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('toast.networkError'));
      e.target.value = '';
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t('company.logoHint'));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => patch({ Logo: String(reader.result || '') });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await MainService.SaveCompanyProfile(profile);
      if (isSaveSuccess(response)) {
        setSuccess(t('toast.saveSuccess'));
        localStorage.setItem('companyProfile', JSON.stringify(profile));
        await loadProfile();
      } else {
        setError(t('toast.saveFailed'));
      }
    } catch (err) {
      console.error('Save company profile:', err);
      setError(t('toast.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm(t('actions.logoutAllConfirm'))) return;
    setLoggingOutAll(true);
    setError('');
    try {
      const response = await MainService.LogOutAll();
      if (isSaveSuccess(response) || response === true) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('companyProfile');
        navigate('/login');
      } else {
        setError(t('toast.logoutFailed'));
      }
    } catch (err) {
      console.error('LogOutAll:', err);
      setError(t('toast.networkError'));
    } finally {
      setLoggingOutAll(false);
    }
  };

  const bindAccount = (nameKey, idKey) => ({
    value: profile[nameKey] || { Name: '' },
    onSelect: (row) => {
      patch({
        [nameKey]: { Id: row.Id, Name: row.Name },
        [idKey]: row.Id,
      });
    },
  });

  const defaultFlags = [
    ['IsSameDay', 'flags.isSameDay'],
    ['IsVATFromDelivery', 'flags.isVatFromDelivery'],
    ['IsBlockByOrderSelection', 'flags.isBlockByOrderSelection'],
    ['IsPickupByComp', 'flags.isPickupByComp'],
    ['IsBlockCancelPay', 'flags.isBlockCancelPay'],
    ['IsKeepEmirate', 'flags.isKeepEmirate'],
    ['IsSMSFromDN', 'flags.isSmsFromDn'],
    ['IsSeperateClientPayment', 'flags.isSeperateClientPayment'],
    ['AllowSameClientNo', 'flags.allowSameClientNo'],
    ['AllowSameCustomerNo', 'flags.allowSameCustomerNo'],
    ['AdjustCPDate', 'flags.adjustCpDate'],
    ['BlockPayRetn', 'flags.blockPayRetn'],
    ['BlockCustomerOrderLoad', 'flags.blockCustomerOrderLoad'],
    ['OnlyDeliveredSMS', 'flags.onlyDeliveredSms'],
    ['HideCODSelection', 'flags.hideCodSelection'],
    ['IsBlockSameStoreVoucherNo', 'flags.isBlockSameStoreVoucherNo'],
    ['IsBlockStoreRefLoad', 'flags.isBlockStoreRefLoad'],
    ['IsHideOrderDelete', 'flags.isHideOrderDelete'],
    ['IsAutoGenerateVoucherNo', 'flags.isAutoGenerateVoucherNo'],
    ['IsHidePaymentEditOnShipment', 'flags.isHidePaymentEditOnShipment'],
    ['IsHideDeliveryEditOnShipment', 'flags.isHideDeliveryEditOnShipment'],
    ['IsHidePaymentEditOnOrder', 'flags.isHidePaymentEditOnOrder'],
    ['IsHideDeliveryEditOnOrder', 'flags.isHideDeliveryEditOnOrder'],
    ['IsMaskClientMobNo', 'flags.isMaskClientMobNo'],
    ['IsBlockExcelExport', 'flags.isBlockExcelExport'],
    ['IsBlockPdfReport', 'flags.isBlockPdfReport'],
    ['IsBlockIncomeInShipment', 'flags.isBlockIncomeInShipment'],
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <Building2 className="h-8 w-8 shrink-0 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
              <p className="mt-1 text-sm text-gray-600">{t('subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? t('actions.saving') : t('actions.save')}
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        ) : null}
        {success ? (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        ) : null}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max px-2" aria-label="Company profile tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {activeTab === TAB_COMPANY ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SettingsField label={t('company.name')}>
                      <input className={inputClass} value={profile.Name || ''} onChange={(e) => patch({ Name: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('company.mobile')}>
                      <input className={inputClass} value={profile.MobileNo || ''} onChange={(e) => patch({ MobileNo: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('company.telephone')}>
                      <input className={inputClass} value={profile.TelNo || ''} onChange={(e) => patch({ TelNo: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('company.email')}>
                      <input type="email" className={inputClass} value={profile.EmailId || ''} onChange={(e) => patch({ EmailId: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('company.website')}>
                      <input className={inputClass} value={profile.Website || ''} onChange={(e) => patch({ Website: e.target.value })} />
                    </SettingsField>
                    <SettingsField label={t('company.address')} className="md:col-span-2">
                      <input className={inputClass} value={profile.Address || ''} onChange={(e) => patch({ Address: e.target.value })} />
                    </SettingsField>
                  </div>

                  <SettingsField label={t('company.logo')}>
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                        {logoPreview ? (
                          <img src={logoPreview} alt="" className="max-h-full max-w-full object-contain" />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                      <div>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                        <button
                          type="button"
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
                          onClick={() => fileRef.current?.click()}
                        >
                          {t('actions.chooseLogo')}
                        </button>
                        <p className="mt-2 text-xs text-gray-500">{t('company.logoHint')}</p>
                      </div>
                    </div>
                  </SettingsField>

                  <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4">
                    <p className="text-sm font-medium text-amber-900">{t('actions.logoutAll')}</p>
                    <p className="mt-1 text-xs text-amber-800">{t('actions.logoutAllHint')}</p>
                    <button
                      type="button"
                      onClick={handleLogoutAll}
                      disabled={loggingOutAll}
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                    >
                      {loggingOutAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                      {t('actions.logoutAll')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeTab === TAB_DEFAULTS ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SettingsField label={t('defaults.defDelCharge')}>
                      <input
                        type="number"
                        className={inputClass}
                        value={profile.DefDelCharge ?? 0}
                        onChange={(e) => patch({ DefDelCharge: Number(e.target.value) || 0 })}
                      />
                    </SettingsField>
                    <SettingsField label={t('defaults.defaultType')}>
                      <select
                        className={inputClass}
                        value={profile.DefaultTypeId || ''}
                        onChange={(e) => patch({ DefaultTypeId: e.target.value })}
                      >
                        <option value="">{t('defaults.selectType')}</option>
                        {types.map((row) => (
                          <option key={row.Id} value={row.Id}>
                            {row.Name}
                          </option>
                        ))}
                      </select>
                    </SettingsField>
                    <SettingsField label={t('defaults.defTax')}>
                      <input
                        type="number"
                        className={inputClass}
                        value={profile.DefTAX ?? 0}
                        onChange={(e) => patch({ DefTAX: Number(e.target.value) || 0 })}
                      />
                    </SettingsField>
                    <SettingsField label={t('defaults.defPayType')}>
                      <select className={inputClass} value={profile.DefPayType || ''} onChange={(e) => patch({ DefPayType: e.target.value })}>
                        <option value="">—</option>
                        <option value="CAD">{t('defaults.payCad')}</option>
                        <option value="COD">{t('defaults.payCod')}</option>
                      </select>
                    </SettingsField>
                    <SettingsField label={t('defaults.defChargeFrom')}>
                      <select className={inputClass} value={profile.DefChargeFrom || ''} onChange={(e) => patch({ DefChargeFrom: e.target.value })}>
                        <option value="">—</option>
                        <option value="CLIENT">{t('defaults.chargeClient')}</option>
                        <option value="AREA">{t('defaults.chargeArea')}</option>
                      </select>
                    </SettingsField>
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {defaultFlags.map(([key, labelPath]) => (
                      <SettingsCheckbox
                        key={key}
                        label={t(labelPath)}
                        checked={profile[key]}
                        onChange={(v) => setFlag(key, v)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {activeTab === TAB_SMS ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {[
                    ['SMSUSername', 'sms.smsUser'],
                    ['SMSPassword', 'sms.smsPassword'],
                    ['SMSId', 'sms.smsId'],
                    ['EmailUsername', 'sms.emailUser'],
                    ['EmailPassword', 'sms.emailPassword'],
                    ['FireApiKey', 'sms.fireApiKey'],
                    ['FireAuthDomain', 'sms.fireAuthDomain'],
                    ['FireProjectId', 'sms.fireProjectId'],
                    ['FireStorageBucket', 'sms.fireStorageBucket'],
                    ['FireMessagingSenderId', 'sms.fireMessagingSenderId'],
                    ['FireAppId', 'sms.fireAppId'],
                    ['FireMeasurementId', 'sms.fireMeasurementId'],
                    ['FireServerKey', 'sms.fireServerKey'],
                  ].map(([field, labelPath]) => (
                    <SettingsField key={field} label={t(labelPath)}>
                      <input
                        className={inputClass}
                        type={field.includes('Password') || field.includes('Key') ? 'password' : 'text'}
                        value={profile[field] || ''}
                        onChange={(e) => patch({ [field]: e.target.value })}
                        autoComplete="off"
                      />
                    </SettingsField>
                  ))}
                </div>
              ) : null}

              {activeTab === TAB_ACCOUNT && profile.IsAccounting ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <SettingsField label={t('account.financialYear')}>
                    <select
                      className={inputClass}
                      value={profile.FinancialYearId || ''}
                      onChange={(e) => patch({ FinancialYearId: e.target.value })}
                    >
                      <option value="">—</option>
                      {finYears.map((y) => (
                        <option key={y.Id} value={y.Id}>
                          {y.Name}
                        </option>
                      ))}
                    </select>
                  </SettingsField>
                  <SettingsField label={t('account.inputVat')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DefaultInputVat', 'DefaultInputVatId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.outputVat')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DefaultOutputVat', 'DefaultOutputVatId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.clientHead')}>
                    <AccountAutocomplete
                      level3
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DefaultClientHead', 'DefaultClientHeadId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.employeeHead')}>
                    <AccountAutocomplete
                      level3
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DefaultEmployeeHead', 'DefaultEmployeeHeadId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.expenseHead')}>
                    <AccountAutocomplete
                      level3
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DefaultExpenseHead', 'DefaultExpenseHeadId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.customerAccount')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('CustomerAccount', 'CustomerAccountId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.deliveryRevenue')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('DeliveryRevenewAcc', 'DeliveryRevenewAccId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.cashAccount')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('CashAccount', 'CashAccountId')}
                    />
                  </SettingsField>
                  <SettingsField label={t('account.bankAccount')}>
                    <AccountAutocomplete
                      placeholder={t('account.searchAccount')}
                      {...bindAccount('BankAccount', 'BankAccountId')}
                    />
                  </SettingsField>
                </div>
              ) : null}

              {activeTab === TAB_SUPER && isSa ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    ['IsAccounting', 'super.isAccounting'],
                    ['IsHR', 'super.isHr'],
                    ['IsMap', 'super.isMap'],
                    ['IsPushNotifications', 'super.isPushNotifications'],
                    ['IsVATFromCustomerOnly', 'super.isVatFromCustomerOnly'],
                    ['IsOtp', 'super.isOtp'],
                  ].map(([key, labelPath]) => (
                    <SettingsCheckbox
                      key={key}
                      label={t(labelPath)}
                      checked={profile[key]}
                      onChange={(v) => setFlag(key, v)}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
